import { useEffect } from "react";
import { recordDebug } from "./debug";

// lib.dom in our TS version predates the Screen Wake Lock API.
type WakeLockSentinel = { release: () => Promise<void> };
type WakeLockNavigator = Navigator & {
  wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinel> };
};

/**
 * Keeps the screen on while `enabled`. The browser drops the lock whenever the
 * page is hidden, so it is re-acquired when the page becomes visible again.
 */
export const useWakeLock = (enabled: boolean) => {
  useEffect(() => {
    const wakeLock = (navigator as WakeLockNavigator).wakeLock;
    if (!enabled) return;
    if (!wakeLock) {
      recordDebug({ wakeLock: "unsupported" });
      return;
    }

    let sentinel: WakeLockSentinel | undefined;
    let cancelled = false;

    const request = async () => {
      try {
        const lock = await wakeLock.request("screen");
        if (cancelled) {
          lock.release().catch(() => {});
        } else {
          sentinel = lock;
          recordDebug({ wakeLock: "held" });
        }
      } catch (e) {
        // Denied (e.g. low battery mode); the show still runs, the screen may dim.
        recordDebug({ wakeLock: "denied" });
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") request();
    };

    request();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      sentinel?.release().catch(() => {});
      recordDebug({ wakeLock: "released" });
    };
  }, [enabled]);
};

/**
 * Colors the browser chrome around the takeover (status bar and toolbar
 * areas) while joined. Browsers tint these from `theme-color` or, in newer
 * iOS Safari, from the page's root background, so both are set and then
 * restored on exit.
 */
export const useChromeColor = (color: string | undefined) => {
  useEffect(() => {
    if (!color) return;
    const meta = document.querySelector('meta[name="theme-color"]');
    const previousMeta = meta?.getAttribute("content");
    const { documentElement: html, body } = document;
    const previousHtml = html.style.backgroundColor;
    const previousBody = body.style.backgroundColor;

    meta?.setAttribute("content", color);
    html.style.backgroundColor = color;
    body.style.backgroundColor = color;

    return () => {
      if (previousMeta) meta?.setAttribute("content", previousMeta);
      html.style.backgroundColor = previousHtml;
      body.style.backgroundColor = previousBody;
    };
  }, [color]);
};
